import React, { useMemo, useState } from 'react';
import { XMarkIcon, LanguageIcon } from '../../icons';
import LanguageSelect from '../recording/LanguageSelect';
import { DEFAULT_LANGUAGES } from '../../config/languages';
import { REGENERATION_REASONS } from '../../utils/regeneration';
import { getLanguageLabel } from '../../utils/languages';

const RegenerateModal = ({
  open,
  onClose,
  onSubmit,
  submitting = false,
  error = '',
  existingLanguages = [],
  sourceLanguage = '',
  languages = null,
}) => {
  const [targetLanguage, setTargetLanguage] = useState('');
  const [reasonCode, setReasonCode] = useState('');

  const availableLanguages = useMemo(() => {
    if (Array.isArray(languages)) {
      return languages;
    }
    const taken = new Set(
      [...existingLanguages, sourceLanguage, 'English']
        .filter(Boolean)
        .map((lang) => getLanguageLabel(lang).toLowerCase())
    );
    return DEFAULT_LANGUAGES.filter(
      (lang) => lang.label.toLowerCase() !== 'english' && !taken.has(lang.label.toLowerCase())
    );
  }, [existingLanguages, sourceLanguage, languages]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!targetLanguage) return;
    onSubmit({
      target_language: targetLanguage,
      reason_code: reasonCode || undefined,
    });
  };

  const handleClose = () => {
    if (submitting) return;
    setTargetLanguage('');
    setReasonCode('');
    onClose();
  };

  return (
    <div className="md-modal-overlay">
      <div className="md-modal" role="dialog" aria-modal="true" aria-labelledby="regen-modal-title">
        <div className="md-modal-header">
          <div className="flex items-center gap-2">
            <div className="md-icon-well bg-matdash-primary-subtle text-matdash-primary">
              <LanguageIcon className="h-5 w-5" />
            </div>
            <h2 id="regen-modal-title" className="text-lg font-semibold text-matdash-dark mb-0">
              Regenerate
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="text-matdash-muted hover:text-matdash-dark disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="md-modal-body space-y-4">
          <p className="text-sm text-matdash-muted">
            A new version is translated from the English transcript and queued like any
            other transcription job. The current transcript will not be changed.
          </p>

          {availableLanguages.length === 0 ? (
            <p className="md-alert md-alert-warning">
              All supported languages already have versions for this session.
            </p>
          ) : (
            <LanguageSelect
              id="regen-target-language"
              label="Target language"
              value={targetLanguage}
              onChange={setTargetLanguage}
              languages={availableLanguages}
              helperText="Translated from the English transcript. No re-recording required."
            />
          )}

          <div>
            <label
              htmlFor="regen-reason"
              className="block text-sm font-medium text-matdash-dark mb-2"
            >
              Reason (optional)
            </label>
            <select
              id="regen-reason"
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              disabled={submitting}
              className="md-form-control w-full"
            >
              <option value="">Select reason…</option>
              {REGENERATION_REASONS.map((reason) => (
                <option key={reason.code} value={reason.code}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <p className="md-alert md-alert-danger">{error}</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="md-btn md-btn-outline disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !targetLanguage || availableLanguages.length === 0}
              className="md-btn md-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Queuing…' : 'Queue regeneration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegenerateModal;
